import {createFileRoute, notFound, Outlet} from "@tanstack/react-router";
import type {QueryClient} from "@tanstack/react-query";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import type {Result} from "@vesta/error-handling-remix/result";
import {
    organizationalUnitValidator,
    type OrganizationalUnit,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import {
    organizationalUnitTemplateValidator,
    type OrganizationalUnitTemplate,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {
    userValidator,
    type User,
} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import {
    userTemplateValidator,
    type UserTemplate,
} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {getAdminWorkspacesWithCaching} from "../-queries";
import {ErrorComponent} from "../-errorComponent";
import {NotFoundComponent} from "../-notFoundComponent";
import {PendingComponent} from "../-pendingComponent";
import {adminUserDetailValidator, type AdminUserDetail} from "~/domainModels/adminModels/adminUserDetails";
import {getWorkspaceDefinitionsMetadataWithCaching} from "~/utilities/fetchWorkspaceDefinitionsMetadata";
import {
    CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT,
} from "~/utilities/queries";
import {getAdminWorkspaceCapabilitiesWithCaching} from "~/utilities/adminWorkspaceCapabilitiesQuery";
import {
    WORKSPACE__ORGANIZATION_UNIT_TEMPLATES__QUERY_KEY,
    WORKSPACE__ORGANIZATION_UNITS__QUERY_KEY,
    WORKSPACE__USER_TEMPLATES__QUERY_KEY,
    WORKSPACE__USERS__QUERY_KEY,
} from "~/utilities/queryKeys";
import {USER_DETAILS_QUERY_KEY} from "../../user-profiles/route";

export const Route = createFileRoute("/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/a/_adminScaffold/workspace/$workspaceId")({
    loader: async ({context, params}) => {
        const workspaceIdResult = uuidZodValidator.safeParse(params.workspaceId);
        if (workspaceIdResult.success !== true) {
            throw notFound();
        }

        const workspaceId = workspaceIdResult.data;
        const [
            workspaces,
            workspaceCapabilitiesResult,
            workspaceUsersResult,
            workspaceUserTemplatesResult,
            organizationalUnitsResult,
            organizationalUnitTemplatesResult,
            platformUsers,
            workspaceDefinitionsMetadata,
        ] = await Promise.all([
            getAdminWorkspacesWithCaching({
                queryClient: context.queryClient,
                intellsysBaseUrl: context.intellsysBaseUrl,
                accessToken: context.authenticationStatus.accessToken,
            }),
            getAdminWorkspaceCapabilitiesWithCaching({
                workspaceId,
                intellsysBaseUrl: context.intellsysBaseUrl,
                accessToken: context.authenticationStatus.accessToken,
                queryClient: context.queryClient,
            }),
            getAdminWorkspaceUsersWithCaching({
                workspaceId,
                intellsysBaseUrl: context.intellsysBaseUrl,
                accessToken: context.authenticationStatus.accessToken,
                queryClient: context.queryClient,
            }),
            getAdminUserTemplatesWithCaching({
                workspaceId,
                intellsysBaseUrl: context.intellsysBaseUrl,
                accessToken: context.authenticationStatus.accessToken,
                queryClient: context.queryClient,
            }),
            getAdminOrganizationalUnitsWithCaching({
                workspaceId,
                intellsysBaseUrl: context.intellsysBaseUrl,
                accessToken: context.authenticationStatus.accessToken,
                queryClient: context.queryClient,
            }),
            getAdminOrganizationalUnitTemplatesWithCaching({
                workspaceId,
                intellsysBaseUrl: context.intellsysBaseUrl,
                accessToken: context.authenticationStatus.accessToken,
                queryClient: context.queryClient,
            }),
            context.queryClient.ensureQueryData<Array<AdminUserDetail>>({
                queryKey: [USER_DETAILS_QUERY_KEY],
                queryFn: async () => {
                    const result = await robustJsonResultClientFetch(
                        `${context.intellsysBaseUrl}/sub-system/pluto/admin/user-details/get`,
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${context.authenticationStatus.accessToken}`,
                            },
                            body: JSON.stringify({}),
                        },
                        zod.array(adminUserDetailValidator),
                    );
                    if (result.success !== true) {
                        throw result.error;
                    }
                    return result.data;
                },
            }),
            getWorkspaceDefinitionsMetadataWithCaching({
                queryClient: context.queryClient,
                intellsysBaseUrl: context.intellsysBaseUrl,
                accessToken: context.authenticationStatus.accessToken,
            }),
        ]);

        if (workspaceCapabilitiesResult.success !== true) {
            throw workspaceCapabilitiesResult.error;
        }
        if (workspaceUsersResult.success !== true) {
            throw workspaceUsersResult.error;
        }
        if (workspaceUserTemplatesResult.success !== true) {
            throw workspaceUserTemplatesResult.error;
        }
        if (organizationalUnitsResult.success !== true) {
            throw organizationalUnitsResult.error;
        }
        if (organizationalUnitTemplatesResult.success !== true) {
            throw organizationalUnitTemplatesResult.error;
        }

        const workspace = workspaces.find((item) => item.id === workspaceId);
        if (workspace === undefined) {
            throw notFound();
        }

        return {
            workspace,
            workspaceId,
            workspaceCapabilities: workspaceCapabilitiesResult.data,
            workspaceUsers: workspaceUsersResult.data,
            workspaceUserTemplates: workspaceUserTemplatesResult.data,
            organizationalUnits: organizationalUnitsResult.data,
            organizationalUnitTemplates: organizationalUnitTemplatesResult.data,
            platformUsers,
            intellsysBaseUrl: context.intellsysBaseUrl,
            accessToken: context.authenticationStatus.accessToken,
            workspaceDefinitionsMetadata,
        };
    },
    component: Outlet,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});

async function getAdminWorkspaceUsersWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<User>>> {
    return queryClient.ensureQueryData({
        queryKey: [WORKSPACE__USERS__QUERY_KEY, workspaceId, "admin", "get-all", CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT],
        queryFn: () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/admin/users/get-all`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                        ...CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT,
                        workspaceId,
                    }),
                },
                zod.array(userValidator),
            ),
        retry: false,
        staleTime: Infinity,
    });
}

async function getAdminUserTemplatesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<UserTemplate>>> {
    return queryClient.ensureQueryData({
        queryKey: [WORKSPACE__USER_TEMPLATES__QUERY_KEY, workspaceId, "admin"],
        queryFn: () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/admin/user-templates/get`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({workspaceId}),
                },
                zod.array(userTemplateValidator),
            ),
        retry: false,
        staleTime: Infinity,
    });
}

async function getAdminOrganizationalUnitsWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<OrganizationalUnit>>> {
    return queryClient.ensureQueryData({
        queryKey: [WORKSPACE__ORGANIZATION_UNITS__QUERY_KEY, workspaceId, "admin", "get-all", CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT],
        queryFn: () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/admin/organizational-units/get-all`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({
                        ...CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT,
                        workspaceId,
                    }),
                },
                zod.array(organizationalUnitValidator),
            ),
        retry: false,
        staleTime: Infinity,
    });
}

async function getAdminOrganizationalUnitTemplatesWithCaching({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
}): Promise<Result<Array<OrganizationalUnitTemplate>>> {
    return queryClient.ensureQueryData({
        queryKey: [WORKSPACE__ORGANIZATION_UNIT_TEMPLATES__QUERY_KEY, workspaceId, "admin"],
        queryFn: () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/admin/organizational-unit-templates/get`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({workspaceId}),
                },
                zod.array(organizationalUnitTemplateValidator),
            ),
        retry: false,
        staleTime: Infinity,
    });
}
